/**
 * Curation module.
 *
 * Owns curated rooms (see wiki/decisions/2026-10-10-let-owners-curate-a-rooms-games.md):
 * an owner-only switch and an ordered selection of games that participants
 * see instead of the full catalog. Curation shapes what clients offer; the
 * room does not refuse games outside the selection.
 */

import type { RoomStorage, BroadcastChannel, InternalRoomState } from '../../types/infrastructure.types'
import type { RoomCaller } from '../../types/room.types'
import type { SetCuratedGamesArgs, SetCuratedModeArgs } from '../../schemas/action.schemas'

export interface CurationContainer {
  storage: RoomStorage
  broadcastChannel: BroadcastChannel
}

type Result = { success: boolean; error?: string }

export function makeCuration(container: CurationContainer) {
  const { storage, broadcastChannel } = container

  const commit = (changes: Pick<InternalRoomState, 'curated' | 'curatedGameIds'>) => {
    const next = { ...storage.getState(), ...changes }
    storage.setState(next)
    broadcastChannel.broadcast({
      type: 'curationChanged',
      data: { curated: Boolean(next.curated), curatedGameIds: next.curatedGameIds ?? [] },
    })
  }

  const setCuratedMode = async (args: SetCuratedModeArgs, caller?: RoomCaller): Promise<Result> => {
    if (!caller?.isOwner) return { success: false, error: 'Only the room owner can curate games' }
    commit({ curated: args.curated })
    return { success: true }
  }

  /** Replaces the whole ordered selection, so adding, removing, and reordering are one operation. */
  const setCuratedGames = async (args: SetCuratedGamesArgs, caller?: RoomCaller): Promise<Result> => {
    if (!caller?.isOwner) return { success: false, error: 'Only the room owner can curate games' }
    commit({ curatedGameIds: [...new Set(args.gameIds)] })
    return { success: true }
  }

  return { setCuratedMode, setCuratedGames }
}
